import { Heading1, Paragraph } from '@/components/Typography';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { fetchGeneratedStory } from '@/api/services/generated-stories/fetchGeneratedStory';
import { updateGeneratedStory } from '@/api/services/generated-stories/updateGeneratedStory';
import type { GeneratedStoryWithImageUrls } from 'schemas';
import { toast } from 'sonner';
import { checkoutPageMessages } from './checkoutPage.messages';
import { useLoadOnce } from '@/hooks/useLoadOnce';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { ROUTES } from '@/routes';

export function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const storyId = searchParams.get('storyId');
  const [generatedStory, setGeneratedStory] =
    useState<GeneratedStoryWithImageUrls | null>(null);
  const [loading, setLoading] = useState(false);
  const [generatingStory, setGeneratingStory] = useState(false);

  useLoadOnce(() => {
    if (!storyId) return;

    const loadStory = async () => {
      setLoading(true);
      try {
        // Fetch the specific story by ID
        const story = await fetchGeneratedStory(storyId);
        setGeneratedStory(story);
      } catch (error) {
        toast.error(checkoutPageMessages.toastFailedToLoad);
        console.error('Error loading story:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStory();
  });

  const handleCompleteCheckout = async () => {
    if (!storyId || !generatedStory) return;

    setGeneratingStory(true);
    toast.info(checkoutPageMessages.toastGeneratingStory);

    try {
      const updatedStory = await updateGeneratedStory(storyId);
      if (updatedStory.story) {
        toast.success(checkoutPageMessages.toastStoryGenerated);
        // Redirect to generated stories page after successful generation
        navigate(ROUTES.DASHBOARD_GENERATED_STORIES);
      } else {
        toast.error(checkoutPageMessages.toastStoryGenerationFailed);
      }
    } catch {
      toast.error(checkoutPageMessages.toastStoryGenerationFailed);
    } finally {
      setGeneratingStory(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!storyId) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Heading1>{checkoutPageMessages.title}</Heading1>
          <Paragraph className="mt-4 text-lg text-stone-600">
            {checkoutPageMessages.noStoryIdProvided}
          </Paragraph>
        </div>
      </div>
    );
  }

  if (!generatedStory) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Heading1>{checkoutPageMessages.title}</Heading1>
          <Paragraph className="mt-4 text-lg text-stone-600">
            {checkoutPageMessages.storyNotFound}
          </Paragraph>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="space-y-4 text-center">
        <Heading1>{checkoutPageMessages.title}</Heading1>
        <Paragraph className="text-lg text-stone-600">
          {checkoutPageMessages.checkoutWithStory.replace(
            '{title}',
            generatedStory.title
          )}
        </Paragraph>
        <Paragraph className="text-sm text-stone-500">
          {checkoutPageMessages.storyId.replace('{id}', storyId)}
        </Paragraph>

        <div className="pt-4">
          <Button
            onClick={handleCompleteCheckout}
            disabled={generatingStory}
            size="lg"
            className="min-w-[200px]"
          >
            {generatingStory ? (
              <>
                <LoadingSpinner className="mr-2" size="sm" isDark={false} />
                {checkoutPageMessages.generatingStory}
              </>
            ) : (
              checkoutPageMessages.completeCheckoutButton
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
